/* eslint-disable */
/**
 * Permission Mixin
 *
 * This mixin provides a simple permission checking system using string-based permissions.
 * Permissions are stored as strings in format: "MODULE.PERMISSION" (e.g., "PRODUCTS.CREATE")
 *
 * Usage in component:
 * import permissionMixin from '@/mixins/permissionMixin'
 *
 * export default {
 *   mixins: [permissionMixin],
 *   // ... component code
 * }
 *
 * Then use in template:
 * v-if="can('PRODUCTS.CREATE')"
 * v-if="can('USERS.UPDATE')"
 * v-if="can('ORDERS.DELETE')"
 *
 * Available permission types:
 * - MODULE.VIEW          - Can view list/index page
 * - MODULE.SINGLE_VIEW   - Can view single record details
 * - MODULE.CREATE        - Can create new records
 * - MODULE.UPDATE        - Can edit existing records
 * - MODULE.DELETE        - Can delete records
 * - MODULE.MASTERS       - Can access master data
 */

import { mapState } from 'vuex'

export default {
  computed: {
    ...mapState('auth', ['user', 'permissions']),

    /**
     * Get the current user's role name
     * @returns {string} Role name (e.g., 'ADMIN', 'SUPER ADMIN', 'USER')
     */
    userRole() {
      if (!this.user || !this.user.roles || !this.user.roles.length) {
        return null
      }
      return this.user.roles[0].name
    },

    /**
     * Get the current user's position name
     * @returns {string} Position name (e.g., 'CUSTOMER', 'DISTRIBUTOR/DEALER', 'SALE EXECUTIVE')
     */
    positionName() {
      if (!this.user || !this.user.position) {
        return null
      }
      return this.user.position.name
    },
  },

  methods: {
    /**
     * Check if the current user has a specific permission
     *
     * ADMIN and SUPER ADMIN always return true (bypass permission checks)
     *
     * @param {string} permission - Permission string in format "MODULE.PERMISSION" (e.g., "PRODUCTS.CREATE")
     * @returns {boolean} True if user has the permission, false otherwise
     *
     * @example
     * // In template
     * <v-btn v-if="can('PRODUCTS.CREATE')">Create Product</v-btn>
     * <v-btn v-if="can('USERS.UPDATE')">Edit User</v-btn>
     * <v-btn v-if="can('ORDERS.DELETE')">Delete Order</v-btn>
     *
     * // In script methods
     * if (this.can('PRODUCTS.CREATE')) {
     *   this.$router.push('/products/create')
     * }
     */
    can(permission) {
      if (!permission) return false

      // SUPER ADMIN and ADMIN bypass all permission checks
      if (this.userRole === 'SUPER ADMIN' || this.userRole === 'ADMIN') {
        return true
      }

      // Check if permission string exists in permissions array
      return this.permissions && this.permissions.includes(permission)
    },

    /**
     * Check if user CANNOT perform an action
     * @param {string} permission - Permission string (e.g., "PRODUCTS.CREATE")
     * @returns {boolean}
     */
    cannot(permission) {
      return !this.can(permission)
    },

    /**
     * Check if user has ANY of the given permissions
     * @param {Array<string>} permissionArray - Array of permission strings
     * @returns {boolean}
     *
     * @example
     * // Show button if user can either create or update products
     * <v-btn v-if="canAny(['PRODUCTS.CREATE', 'PRODUCTS.UPDATE'])">Manage Products</v-btn>
     */
    canAny(permissionArray) {
      return permissionArray.some(permission => this.can(permission))
    },

    /**
     * Check if user has ALL of the given permissions
     * @param {Array<string>} permissionArray - Array of permission strings
     * @returns {boolean}
     *
     * @example
     * // Show button only if user has both permissions
     * <v-btn v-if="canAll(['PRODUCTS.UPDATE', 'PRODUCTS.DELETE'])">Full Management</v-btn>
     */
    canAll(permissionArray) {
      return permissionArray.every(permission => this.can(permission))
    },
  },
}
